import { useEffect, useRef } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { HardDrive, KeyRound } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/react';
import { startSignIn, useSession } from '@/lib/auth/session';
import { useStorageFull } from '@/lib/db/quota';
import { applyUpdate, dismissUpdate, usePwaState } from '@/lib/pwa/register';
import { useSyncStatus } from '@/lib/sync';
import { toast } from '@/lib/toast';
import { i18next } from '@/i18n';

/**
 * Shown when the server refused to refresh the session. Work stays on the
 * device (nothing queued is dropped) until the person signs in again.
 */
export function ReauthBanner() {
  const t = useT();
  const { status } = useSession();
  const { counts } = useSyncStatus();
  const location = useLocation();
  if (status !== 'reauth_required') return null;
  const queued = counts.pending + counts.failed + counts.in_flight;
  return (
    <div className="notice" role="alert">
      <KeyRound size={18} aria-hidden="true" />
      <span className="flex-1">
        {queued
          ? t('Your session has ended. Sign in to sync {{count}} saved changes.', { count: queued })
          : t('Your session has ended. Sign in to sync.')}
      </span>
      <Button
        variant="ink"
        size="sm"
        onClick={() => {
          void startSignIn(location.href).catch(() => toast.error('Sign-in needs a connection.'));
        }}
      >
        {t('Sign in to sync')}
      </Button>
    </div>
  );
}

/**
 * Shown while the device refuses local writes for lack of space. The refused
 * change was not saved; everything saved before it (and the outbox) is intact.
 */
export function StorageFullBanner() {
  const t = useT();
  const full = useStorageFull();
  const announced = useRef(false);

  useEffect(() => {
    if (full && !announced.current) {
      toast.error('Storage full: this change was not saved. Nothing saved earlier is lost.', {
        id: 'storage-full',
      });
    }
    announced.current = full;
  }, [full]);

  if (!full) return null;
  return (
    <div className="notice" role="alert" data-testid="storage-full">
      <HardDrive size={18} aria-hidden="true" />
      <span className="flex-1">
        {t(
          'This device is out of storage. New changes cannot be saved until space is freed. Everything already saved is safe and will still sync.',
        )}
      </span>
      <Link to="/settings" className="secondary-link">
        {t('Check storage')}
      </Link>
    </div>
  );
}

/** "New version available" prompt; never reloads under the user. */
export function UpdatePrompt() {
  const { needRefresh, offlineReady } = usePwaState();

  useEffect(() => {
    if (!needRefresh) return;
    toast(i18next.t('A new version of KorOS Build is available.'), {
      id: 'pwa-update',
      duration: Infinity,
      action: { label: i18next.t('Reload'), onClick: () => void applyUpdate() },
      onDismiss: dismissUpdate,
    });
  }, [needRefresh]);

  useEffect(() => {
    if (offlineReady) toast.success('KorOS Build is ready to work offline.');
  }, [offlineReady]);

  return null;
}
